<template lang="pug">
.form-select(:class='formClasses')
  Label(:label='label', :required='required')
    input-hidden(:value='value', :name='name')
    Container.form-select__value(
      :class="{ 'form-select__value_disabled': disabled }",
      :cursor='readonly',
      :noTopMargin='!label.length',
      @select.stop='toggle'
    )
      .form-select__button(
        v-if='!readonly',
        :class='{ "form-select__button_opened": opened }'
      )
      // показываем если не только для чтения, если clearable, а так же если value не пусто и если это массив то length > 0
      .form-select__button_clearable(
        v-if='!readonly && clearable && !!(Array.isArray(value) ? value.length > 0 : true) && !!value',
        @click.stop='clearData'
      )
      .form-select__selected(
        v-if='selected',
        :title='selected',
        :class='{ "form-select__selected_scroll": scroll, "form-select__selected_clearable": clearable }'
      ) {{ selected }}
      .form-select__placeholder(v-else) {{ placeholder }}
    Container.form-select__options(v-if='!readonly', :class='containerClasses')
      .form-select__filter-container(v-if='filtering', @click.stop='')
        input.form-select__filter-input(
          ref='input',
          v-model='filter',
          @click.stop=''
        )
      .form-select__no-options(v-if='filtering && filtered.length === 0') {{ filterText }}
      .form-select__options-container(:class='scrollClasses', :style='styles')
        .form-select__options-scroll(
          ref='options',
          v-if='(filtered && filtered.length) || addText'
        )
          .form-select__option(
            v-for='(option, index) in filtered',
            :class='{ "form-select__option_selected": isSelected(option.value), "form-select__option_disable": option.isDisabled }',
            :key='index',
            @click.stop='!option.isDisabled ? input(option) : null'
          ) {{ option.text }}
          .form-select__add(v-if='addText', @click='$emit("add")') {{ addText }}
        .form-select__options-empty(v-else-if='emptyText') {{ emptyText }}
  .form-select__text(v-if='vError') {{ errorText }}
</template>

<script>
import InputHidden from '@/components/form/Hidden.vue';
import { Label } from '../components';
import { Container } from './components';
import isArrayNotEmpty from '@/tools/isarraynotempty';

export default {
  components: {
    InputHidden,
    Label,
    Container,
  },

  props: {
    autoWidth: {
      type: Boolean,
      default: false,
    },

    error: {
      type: [Boolean, String],
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    defaultValue: {
      type: Object | null,
      default: null
    },

    scroll: {
      type: Boolean,
      default: false,
    },

    errorText: {
      type: String,
      default: '',
    },

    emptyText: {
      type: String,
      default: '',
    },

    filterable: {
      type: [Boolean, String],
      default: false,
      validator: (value) => typeof value === 'boolean' || value === 'auto',
    },

    filterText: {
      type: String,
      default: 'Уточните критерии поиска',
    },

    asyncSearch: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: '',
    },

    large: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    name: {
      type: [Boolean, String],
      default: false,
    },

    options: {
      type: Array,
      default: () => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    addText: {
      type: String,
      default: '',
    },

    size: {
      type: Number,
      default: 8,
    },

    value: {
      type: [Number, String, Array],
      default: null,
    },

    wide: {
      type: Boolean,
      default: false,
    },

    quarter: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    resetFilter: {
      type: Boolean,
      default: false,
    },

    v: {
      type: [Object, Function],
      default: null,
    },
  },

  data: () => ({
    filter: '',
    height: null,
    opened: false,
    data: [],
  }),

  computed: {
    vError() {
      return this.error || (this.v && this.v.$error) || (this.v && this.v.$invalid);
    },

    styles() {
      const { height } = this;
      return { maxHeight: height === null ? 'auto' : `${height}px` };
    },

    containerClasses() {
      const { opened, disabled } = this;
      return {
        'form-select__value_disabled': disabled,
        'form-select__options_hidden': !opened,
        'form-select__options_opened': opened,
      };
    },

    filtered() {
      const {
        filtering,
        filter,
        filter: { length },
        options,
        data,
        dataIds,
        multiple,
        asyncSearch,
      } = this;
      if (!filtering || (length === 0 && data.length === 0)) return options;
      const filteredOptions = options.filter(({ value, text }) => {
        const filterAllow =
          value && text.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        if (multiple) {
          if (asyncSearch) return dataIds.indexOf(value) === -1;
          return filterAllow && dataIds.indexOf(value) === -1;
        }
        return asyncSearch || filterAllow;
      });
      return data.concat(filteredOptions);
    },

    filtering() {
      const {
        filterable,
        options: { length },
        size,
      } = this;
      return typeof filterable === 'boolean'
        ? filterable
        : filterable === 'auto' && length > size;
    },

    formClasses() {
      const { hasError, vError, large, wide, quarter } = this;
      return {
        'form-select_wide': wide,
        'form-select_large': large,
        'form-select_has-error': vError || hasError,
        'form-select_quarter': quarter,
      };
    },

    hasError() {
      return this.error && !this.value;
    },

    scrollClasses() {
      const {
        filtered: { length },
        size,
      } = this;
      return {
        'form-select__options_scroll': length > size,
      };
    },

    selected() {
      if (!this.options) {
        return;
      }

      if (!this.multiple) {
        let selected = this.options.filter(
          (item) => item.value !== undefined && item.value == this.value
        );
        return selected.length > 0 ? String(selected[0].text) : undefined;
      } else {
        return this.data.map((item) => item.text).join(', ');
      }
    },

    dataIds() {
      return this.data.map((item) => item.value);
    },
  },

  watch: {
    opened(value) {
      if (!value) {
        this.filter = '';
        return;
      }

      this.$nextTick(() => {
        const { input } = this.$refs;
        if (input) {
          input.focus();
        }
      });
    },

    filter(value) {
      this.$emit('filter', value);
    },

    value(value) {
      this.assignValues();
    },
  },

  mounted() {
    if (document) {
      document.addEventListener('click', this.close);
    }
    this.assignValues();
  },

  beforeDestroy() {
    if (document) {
      document.removeEventListener('click', this.close);
    }
  },

  methods: {
    close() {
      this.opened = false;
    },

    input(option) {
      if (!this.multiple) {
        this.opened = false;
        this.$emit('input', option.value);
      } else {
        const index = this.dataIds.indexOf(option.value);
        if (index === -1) {
          this.data.push(option);
          if (this.resetFilter) this.filter = '';
        } else {
          this.data.splice(index, 1);
        }
        this.$emit('input', this.dataIds);
      }
    },

    open() {
      this.opened = true;
    },

    setHeight() {
      const { options } = this.$refs;
      if (!options) {
        this.height = null;
        return;
      }

      const $el = options;
      const nodes = $el.childNodes;
      const diff = $el.offsetHeight - $el.clientHeight || 0;
      const firstNodes = Array.from(nodes).slice(
        0,
        Math.min(nodes.length, this.size)
      );
      this.height = firstNodes.reduce(
        (sum, { clientHeight }) => sum + clientHeight,
        diff
      );
    },

    toggle() {
      let current = this.opened;
      // Иммитируем клик, чтобы закрыть другие селекты
      if (document) {
        document.getElementsByTagName('body')[0].click();
      }
      this.opened = !current;
      this.$nextTick(() => this.setHeight());
    },

    isSelected(value) {
      return this.dataIds.indexOf(value) !== -1;
    },

    clearData() {
      // TODO: продумать механизм возврата defaultValue для multiple если появится необходимость
      if (!this.multiple) {
        this.opened = false;
        this.$emit('input', this.defaultValue);
      } else {
        this.opened = false;
        this.$emit('input', []);
      }
    },

    assignValues() {
      const { multiple, value } = this;
      if (!multiple) return;
      if (isArrayNotEmpty(value)) {
        this.options.forEach((item) => {
          if (value.includes(item.value) && !this.dataIds.includes(item.value))
            this.data.push(item);
        });
      } else {
        this.data = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-select {
  position: relative;
  display: inline-block;

  .form-select__option_disable{
    color: #828282;
    &:hover{
      background-color: #dddddd;
    }
  }

  .form-select__value_disabled {
    border: #828282;
    background-color: #ededed;
    &:hover{
      border: #828282;
      background-color: #ededed;
    }
  }

  &__add {
    width: 100%;
    height: 28px;
    background-color: #23bb67;
    font-size: 10px;
    font-weight: 700;
    line-height: 28px;
    text-align: center;
    text-transform: uppercase;
    color: #fff;
  }

  &_has-error {
    .form-select__value {
      border-color: red;
    }

    .form-select__text {
      color: red;
      font-size: 0.75em;
      position: absolute;
      top: 58px;
    }

    .form-select__placeholder {
      color: red;
    }
  }

  .form-select__button {
    margin-top: 0px;
    height: 100%;
    width: 1.5em;
    right: 0;
    position: absolute;
    background: url('./img/down.svg') center center no-repeat;
    transition: transform 0.2s ease;

    &_opened {
      transform: rotate(0.5turn);
    }

    &_cursor {
      cursor: default;
    }
  }

  .form-select__button_clearable {
    margin-top: 0px;
    height: 100%;
    width: 1.5em;
    right: 21px;
    position: absolute;
    background: url('./img/clear.svg') center center no-repeat;
    transition: transform 0.2s ease;
    cursor: pointer;
  }

  &_wide {
    width: 100%;
  }

  &_quarter {
    width: calc(100% - 15px);
    @media only screen and (max-width: 40em) {
      width: 100%;
    }
  }

  &_large {
    .form-select__option,
    .form-select__selected,
    .form-select__placeholder {
      font-weight: normal;
    }

    .form-select__selected,
    .form-select__placeholder {
      padding: 5px 10px;
    }

    .form-select__placeholder {
      font-size: 11px;
    }

    .form-select__selected {
      font-size: 14px;
      text-transform: none;
    }
  }

  &__option,
  &__selected,
  &__placeholder {
    font-weight: 400;
    font-size: 0.875rem;
    padding-left: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: none;
  }

  &__selected,
  &__placeholder {
    padding-right: 26px;
    white-space: nowrap;
  }

  &__selected {
    &_scroll {
      max-height: 57px;
      white-space: normal;
      overflow-y: scroll;
    }
  }

  &__selected_clearable {
    padding-right: 0;
    width: 88%;
  }

  &__option,
  &__selected {
    color: #161616;
  }

  &__option {
    line-height: 1.2em;
    padding-right: 10px;
    padding-top: 6px;
    padding-bottom: 6px;

    &:hover {
      background-color: #5897fb;
      color: #fff;
    }

    &_selected {
      background-color: #2c7dfd;
      color: #fff;
    }
    &_selected:hover {
      background-color: #336bc5;
    }
  }

  &__placeholder {
    color: #878787;
  }

  & &__options {
    min-width: 100%;
    display: none;
    position: absolute;
    z-index: 1000;
    margin-top: 1px;
    overflow-x: hidden;
    overflow-y: hidden;

    &_scroll {
      overflow-y: scroll;
    }

    &_opened {
      display: block;
    }

    &-empty {
      line-height: 1.2em;
      padding-right: 10px;
      padding-top: 6px;
      padding-bottom: 6px;
      font-weight: 400;
      font-size: 0.875rem;
      padding-left: 6px;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: none;
      color: #161616;
    }
  }

  &__no-options {
    font-style: italic;
    font-size: 10px;
    text-align: center;
    color: #222;
    padding-bottom: 4px;
  }

  &__filter {
    &-container {
      box-sizing: border-box;
      padding: 3px;
      font-size: 10px;
      line-height: 1em;
      width: 100%;
      z-index: 3;
    }

    &-input {
      box-sizing: border-box;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
      width: 100%;
      border: 1px solid #ddd;
      border-radius: 2px;
      line-height: 125%;
      font-weight: 700;
      font-size: 10px;
      padding: 6px 6px;
      color: #666;
      text-transform: uppercase;
      transition: box-shadow 0.2s ease;

      &:focus {
        outline: none;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
      }
    }
  }

  &__options-container {
    overflow-y: auto;
  }
}
</style>



// WEBPACK FOOTER //
// Select.vue?a8f1d9ae